import { DateValue } from '@internationalized/date';
import { Fragment, ReactElement } from 'react';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { ProviderSearchResultsSkeleton } from '@provider-directory/components/search-results-skeleton/search-results-skeleton.component';
import { useSearch } from '@provider-directory/contexts/ProviderSearchContext';
import { type ProvidersSearchCriteria } from '@provider-directory/types/providers.contracts';
import { NoneInSubDirectory } from './none-in-subdirectory';
import Pagination from './pagination.component';
import ProviderSearchCard from './provider-search-card.component';
import { ProviderSearchEmpty } from './provider-search-empty/provider-search-empty.component';
import {
  OtherSchedulingOptionsSeparator,
  SoftMatchesBanner,
} from './soft-matches-banner';

export interface ProviderSearchResultsProps {
  searchParams: ProvidersSearchCriteria;
  limit?: number;
  startDate?: DateValue | null;
  endDate?: DateValue | null;
  specialtyName?: string;
}

export const ProviderSearchResults = ({
  limit,
  startDate,
  endDate,
}: ProviderSearchResultsProps): ReactElement => {
  const {
    config: { subdirectoryEmptyRedirect },
  } = useOrgContext();

  const {
    pagination,
    providers,
    searchCriteria,
    loadingResults: loading,
    specialtyNames,
    specialtyPresence,
  } = useSearch();

  if (loading) {
    return <ProviderSearchResultsSkeleton />;
  }

  if (!providers || providers.length === 0 || !pagination) {
    const rawRfv =
      searchCriteria['reason-for-visit'] ?? searchCriteria.reasonForVisit;
    const reasonForVisit = Array.isArray(rawRfv) ? rawRfv[0] : rawRfv;

    // RFV empty state takes priority over subdirectory redirect
    if (typeof reasonForVisit === 'string' && reasonForVisit) {
      return <ProviderSearchEmpty reasonForVisit={reasonForVisit} />;
    }

    // Show subdirectory redirect only when the specialty itself doesn't exist
    // in this subdirectory — not when filters narrowed results to zero.
    // The gate is the presence of the config block itself: a directory with no
    // parent to offer simply omits it, so no directory-key lookup is involved.
    if (
      subdirectoryEmptyRedirect &&
      specialtyNames &&
      specialtyPresence === 'not-in-subdirectory'
    ) {
      return (
        <NoneInSubDirectory
          subdirectoryEmptyRedirect={subdirectoryEmptyRedirect}
          specialtyName={specialtyNames[0]}
        />
      );
    }

    return <ProviderSearchEmpty />;
  }

  const exactTotal = pagination.exactTotal ?? 0;
  const softTotal = pagination.softTotal ?? 0;
  const isSoftOnly = exactTotal === 0 && softTotal > 0;
  const firstSoftIndex = providers.findIndex(
    (provider) => provider.matchTier === 'soft',
  );
  const pageLimit = Number(searchCriteria.limit) || Number(limit) || 10;
  const firstSoftGlobalIndex =
    (pagination.page - 1) * pageLimit + firstSoftIndex;
  // AC7: render once at the exact-to-soft boundary, including page boundaries.
  const showSeparatorAt = (index: number): boolean =>
    exactTotal > 0 &&
    firstSoftIndex >= 0 &&
    firstSoftGlobalIndex === exactTotal &&
    index === firstSoftIndex;

  return (
    <div
      className={'duration-500 animate-in fade-in'}
      data-testid={'providers-list'}
    >
      {isSoftOnly ? <SoftMatchesBanner /> : null}
      {providers.map((provider, index) => (
        <Fragment key={provider.npi}>
          {showSeparatorAt(index) ? <OtherSchedulingOptionsSeparator /> : null}
          <ProviderSearchCard
            provider={provider}
            searchCriteria={searchCriteria}
            startDate={startDate}
            endDate={endDate}
            filterSpecialtyDisplayName={specialtyNames?.[0]}
          />
        </Fragment>
      ))}
      <Pagination
        currentPage={pagination.page}
        totalItems={pagination.total}
        pageSize={pagination.items}
        limit={pageLimit}
      />
    </div>
  );
};
